import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useToasts, AppearanceTypes } from "react-toast-notifications";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import { useUser } from "../../../../contexts/userContext/userContext";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectDowngradeAddonErrorMessage,
  selectDowngradeDetails,
  selectPaymentType,
  selectPlanMaxStorage,
} from "../store/purchasesSelectors";
import type { BillingDetailsT, UserDataT } from "../../../../models/models";
import type {
  DowngradeAddonPayloadT,
  DowngradeDetailsT,
  DowngradeSubscriptionPayloadT,
  PurchaseParamsT,
  SubscriptionAddonT,
  SubscriptionT,
} from "../models/models";
import { useModal } from "../../../../contexts/modalContext/modalContext";
import {
  checkIfIsDowngrade,
  externalValidateDowngradeSubscriptions,
} from "../../Purchases/Helper";
import { plansTitleList } from "../../../../utils/jumbomailSettings";
import {
  setDowngradeAddonErrorMessage,
  setDowngradeDetails,
} from "../store/purchasesSlice";
import {
  CurrencyE,
  DowngradeDetailsTypeE,
  PaymentTypeE,
  PlanPackagesE,
} from "../models/enums";
import {
  downgradeAddon,
  downgradeSubscription,
} from "../store/purchasesThunks";
import useAddon from "./useAddon";
import checkStorageIsOverflowed from "../helpers/checkStorageIsOverflowed";
import NotBusinessSubUsersModal from "../../Purchases/ErrorModals/NotBusinessSubUsers";
import NotSubscriberExtraStorage from "../../Purchases/ErrorModals/NotSubscriberExtraStorage";
import DowngradeModal from "../../Purchases/ErrorModals/DowngradeModal";
import StorageOverflowModal from "../modals/StorageOverflowModal";
import type { DowngradeTypeT } from "../modals/StorageOverflowModal/models/models";
import usePlanMaxSubUsers from "./usePlanMaxSubUsers";
import UpdateYearlyToMonthlyDenied from "../modals/UpdateYearlyToMonthlyDenied";
import getCorrectBillingMethod from "../helpers/getCorrectBillingMethod";
import DowngradeNotAllowedModal from "../modals/DowngradeNotAllowedModal";
import useChangePaymentMethodCheck from "./useChangePaymentMethodCheck";

const MAX_FORMS_ALLOWED_NOT_FOR_PREMIUM = 1;

/**
 * Custom React hook for handling downgrade functionality.
 * This hook provides functions and data related to downgrading user subscriptions and addons.
 *
 * @returns An object containing functions and data related to downgrade functionality.
 */

const useDowngrade = () => {
  const { packageType, periodType } = useParams<PurchaseParamsT>();
  const { addToast } = useToasts();
  const { setModal } = useModal();
  const { getTextByKey, getPath } = useContent();
  const toast = useToasts();
  const dispatch = useAppDispatch();
  const paymentType = useAppSelector(selectPaymentType);
  const downgradeDetails = useAppSelector(selectDowngradeDetails);
  const {
    isBusiness,
    isPremium,
    isPro,
    isSubscribed,
    getUserBillingDetails,
    getUserData,
  } = useUser();
  const downgradeAddonErrorMessage = useAppSelector(
    selectDowngradeAddonErrorMessage
  );
  const { addonQuantity, existingAddonQuantity } = useAddon();
  const { checkIfChangePayMethodAllowed } = useChangePaymentMethodCheck();

  /**
   * Determines whether the current purchase is a downgrade
   * based on the package type and addon quantities.
   *
   * @returns {boolean} True if the purchase is a downgrade, false otherwise.
   */
  const isDowngrade =
    packageType === "sub-users" || packageType === "extra-storage"
      ? addonQuantity < existingAddonQuantity
      : checkIfIsDowngrade({
          purchaseType: packageType,
          billingDetails: getUserBillingDetails(),
        });

  /**
   * The maximum storage allowed for the selected plan.
   *
   * @remarks
   * This value is obtained from the Redux store using the `selectPlanMaxStorage` selector.
   */
  const planMaxStorage = useAppSelector((state) =>
    selectPlanMaxStorage(
      state,
      (Object.keys(PlanPackagesE)?.some((plan) => plan === packageType!)
        ? packageType
        : (getUserBillingDetails() as BillingDetailsT)?.type) as PlanPackagesE
    )
  );

  /**
   * The maximum number of sub-users allowed for the current plan.
   */
  const planMaxSubUsers = usePlanMaxSubUsers(
    packageType as unknown as PlanPackagesE
  );

  const billingDetails = getUserBillingDetails() as SubscriptionT;

  /**
   * Validates the downgrade addons for a purchase.
   *
   * @returns {boolean} Returns `true` if the downgrade addons are valid, otherwise `false`.
   */
  const validateDowngradeAddons = () => {
    const paymentTypeWithoutNew =
      paymentType === PaymentTypeE.NEW_CREDIT_CARD
        ? PaymentTypeE.CREDIT_CARD
        : paymentType;

    // First check if exists addons in billing details
    // and if exists check the payment method of the first addon
    if (billingDetails?.addons?.length > 0) {
      /**
       * Determines the last payment method based on the billing details.
       * If the payment method is "pelecard", it returns "creditcard".
       * Otherwise, it returns the lowercase value of the payment method.
       */
      const lastPaymentMethod =
        billingDetails.addons[0].payment_method.toLowerCase() === "pelecard"
          ? "creditcard"
          : billingDetails.addons[0].payment_method.toLowerCase();

      /**
       * Determines the payment type without the "NEW_CREDIT_CARD" option.
       * If the payment type is "NEW_CREDIT_CARD", it is replaced with "CREDIT_CARD".
       * @param {PaymentTypeE} paymentType - The original payment type.
       * @returns {PaymentTypeE} - The payment type without the "NEW_CREDIT_CARD" option.
       */

      const addonTokenId = billingDetails?.addons?.[0]?.token_id;
      const addonProviderId =
        billingDetails?.addons?.[0]?.provider_id === "0"
          ? ""
          : billingDetails?.addons?.[0]?.provider_id;
      if (
        (addonTokenId || addonProviderId) &&
        lastPaymentMethod !== paymentTypeWithoutNew
      ) {
        addToast(getTextByKey("cannot-change-payment-method"), {
          appearance: "error",
        });
        return false;
      }
    }

    switch (packageType!) {
      case "sub-users":
        if (!isBusiness() && !isPremium()) {
          setModal({
            modal: NotBusinessSubUsersModal,
          });
          return false;
        }
        break;
      case "extra-storage":
        if (!isSubscribed()) {
          setModal({
            modal: NotSubscriberExtraStorage,
          });
          return false;
        }
        break;
    }

    const userData = getUserData() as UserDataT;

    if (userData.IsSubscribed) {
      // JW-2217: Check if after downgrade used storage capacity will be more
      // then purchased one or sub users quantity not exceeded future limit.

      /**
       * Indicates whether the number of sub-users has exceeded the maximum allowed limit.
       * @remarks
       * This variable is calculated by comparing the total number of sub-user licenses
       * with the sum of the addon quantity and the maximum allowed sub-users for the plan.
       *
       * @type {boolean}
       */
      const subUsersExceeded =
        addonQuantity > userData?.UserTotalSubUsersLicenses;
      /**
       * Checks if the extra storage exceeds the maximum storage limit.
       *
       * @param {number} userUsedStorage - The amount of storage used by the user.
       * @param {number} planMaxStorage - The maximum storage allowed for the plan.
       * @param {number} addonQuantity - The quantity of addons.
       * @returns {boolean} - Returns true if the extra storage exceeds the maximum storage limit,
       * otherwise false.
       */
      const extraStorageExceeded = checkStorageIsOverflowed(
        userData?.UserUsedStorage,
        +planMaxStorage!,
        addonQuantity
      );

      if (
        ((packageType === "sub-users" && subUsersExceeded) ||
          (packageType === "extra-storage" && extraStorageExceeded)) &&
        isDowngrade
      ) {
        const downgradeType = `${
          subUsersExceeded ? "sub-users" : "extra-storage"
        }-addon` as DowngradeTypeT;

        setModal({
          modal: () => <StorageOverflowModal downgradeType={downgradeType} />,
        });
        return false;
      }
    }

    return true;
  };

  /**
   * Validates the downgrade subscriptions based on the current user details and purchase details.
   * @returns {boolean} - Returns true if the downgrade purchase is allowed, otherwise false.
   */
  const validateDowngradeSubscriptions = (): boolean => {
    const userData = getUserData() as UserDataT;
    const isDowngrade = checkIfIsDowngrade({
      purchaseType: packageType,
      billingDetails,
    });

    // First check if trying to change from yearly to monthly
    /**
     * Represents the current user details.
     * @property {string} billingMethod - The billing method used by the user.
     * @property {number} billingPeriod - The billing period in months.
     * @property {string} userPlan - The user's plan (pro, business, premium).
     * @property {number} userExtraStorage - The number of extra storage units for the user.
     * @property {number} userActualStorage - The amount of storage used by the user.
     * @property {number} userSubUsersLicneses - The total number of sub-user licenses for the user.
     * @property {number} numOfForms - The number of JumboForms owned by the user.
     */
    const currentUserDetails = {
      billingMethod: billingDetails
        ? getCorrectBillingMethod(billingDetails.payment_gateway)
        : "",
      billingPeriod: billingDetails ? billingDetails.period : 0,
      userPlan: isPro()
        ? "pro"
        : isBusiness()
        ? "business"
        : isPremium()
        ? "premium"
        : "",
      userExtraStorage: userData?.NumOfExtraStorage,
      userActualStorage: userData?.UserUsedStorage,
      userSubUsersLicneses: userData?.UserTotalSubUsersLicenses,
      numOfForms: userData?.JumboFormsCount,
      IsFreeProFromCoupon: userData?.IsFreeProFromCoupon,
      isSubscriptionInactive: billingDetails?.status === "INACTIVE",
      autoRenew: billingDetails?.auto_renew,
      token_id: billingDetails?.token_id,
      provider_id: billingDetails?.provider_id,
    };

    const purchaseDetails = {
      paymentMethod:
        paymentType === PaymentTypeE.NEW_CREDIT_CARD
          ? PaymentTypeE.CREDIT_CARD
          : paymentType,
      period: periodType,
      plan: packageType,
    };

    // JW-2308: Not allow downgrade from yearly to monthly anf vice versa
    const periodTypeInMonths = periodType === "monthly" ? 1 : 12;

    if (isDowngrade && periodTypeInMonths !== billingDetails?.period) {
      setModal({
        modal: () => <DowngradeNotAllowedModal />,
      });
      return false;
    }
    const isChangePaymentMethodAllowed = checkIfChangePayMethodAllowed(
      purchaseDetails.paymentMethod!
    );

    if (!isChangePaymentMethodAllowed) {
      return false;
    }

    let result = externalValidateDowngradeSubscriptions({
      currentUserDetails: currentUserDetails,
      purchaseDetails: purchaseDetails,
    });

    if (!result.allowPurchase) {
      if (result.toastMessage) {
        if (result.toastMessage === "cannot-change-yearly-to-monthly") {
          setModal({
            modal: UpdateYearlyToMonthlyDenied,
            modalSize: "full-modal",
          });
        } else {
          addToast(getTextByKey(result.toastMessage), {
            appearance: "error",
          });
        }
      } else {
        setModal({
          modal: DowngradeModal,
          modalSize: "full-modal",
          props: {
            errorType: result.modalName,
          },
        });
      }
    }

    if (
      userData.IsSubscribed &&
      billingDetails?.type !== packageType &&
      isDowngrade
    ) {
      // JW-2217: Check if after downgrade used storage capacity will be more
      // then purchased one or sub users quantity not exceeded future limit.
      const subUsersExceeded =
        userData?.UserTotalSubUsersLicenses > planMaxSubUsers;

      const extraStorageExceeded = checkStorageIsOverflowed(
        userData?.UserUsedStorage,
        +planMaxStorage!,
        existingAddonQuantity
      );

      if ((subUsersExceeded || extraStorageExceeded) && isDowngrade) {
        const downgradeType = `${
          subUsersExceeded ? "sub-users" : "extra-storage"
        }-subscription` as DowngradeTypeT;

        setModal({
          modal: () => <StorageOverflowModal downgradeType={downgradeType} />,
        });
        return false;
      }

      const willFormsCountExceeded =
        userData?.JumboFormsCount > MAX_FORMS_ALLOWED_NOT_FOR_PREMIUM;

      if (willFormsCountExceeded) {
        setModal({
          modal: () => <StorageOverflowModal downgradeType="forms" />,
        });
        return false;
      }
    }

    return result.allowPurchase;
  };

  /**
   * Updates the downgrade details based on the provided parameters.
   * If `isDowngrade` is true and `billingDetails` is available, it calculates the new price,
   * currency, quantity, last payment date, next billing date, and other details for the downgrade.
   * Finally, it dispatches the `setDowngradeDetails` action with the updated downgrade details.
   *
   * @param isDowngrade - Indicates whether the downgrade is requested or not.
   * @param isAddon - Indicates whether the downgrade is for an addon or not.
   * @param type - The type of the downgrade (e.g., addon name or billing details type).
   */
  const updateDowngradeDetails = (
    isDowngrade: boolean,
    isAddon: boolean,
    type: string
  ) => {
    if (isDowngrade && billingDetails) {
      /**
       * Represents the addon used for downgrading a subscription.
       * If the billing details and addon exist, it returns the addon
       * with the specified type and quantity.
       * Otherwise, it returns a default addon object with zero price,
       * zero quantity, and the current date as the last payment try date.
       */
      const downgradeAddon =
        (billingDetails &&
          isAddon &&
          (billingDetails.addons.find(
            (addon) => addon.name === type && addon.quantity > 0
          ) as SubscriptionAddonT)) ||
        ({
          price: 0,
          currency: billingDetails?.currency || CurrencyE.ILS,
          quantity: 0,
          lastPaymentTryDate: new Date().toISOString(),
        } as unknown as SubscriptionAddonT);

      /**
       * The price of the item after downgrade.
       */
      const newPrice = isAddon
        ? downgradeAddon?.price
        : billingDetails.pricePerPeriod;

      const currency = isAddon
        ? downgradeAddon?.currency
        : billingDetails?.currency;
      const symbolCurrency =
        currency === "USD" ? "$" : currency === "EUR" ? "€" : "₪";

      /**
       * The formatted string representing the price with currency symbol and currency code.
       *
       * @remarks
       * This string is used to display the price with the corresponding currency symbol and code.
       */
      const strPrice = `${symbolCurrency} ${newPrice}(${currency})`;

      const newQuantity = isAddon ? downgradeAddon.quantity : 0;
      const lastPaymentDate = new Date(
        isAddon
          ? downgradeAddon.lastPaymentTryDate
          : billingDetails.lastPaymentDate
      );
      const isYearly = !isAddon && billingDetails.period === 12;

      const nextBillingDate = new Date(
        isYearly
          ? lastPaymentDate.setFullYear(lastPaymentDate.getFullYear() + 1)
          : lastPaymentDate.setMonth(lastPaymentDate.getMonth() + 1)
      );

      const downgradeName = isAddon ? type : billingDetails.type;

      const downgradeDetailsTemp: DowngradeDetailsT = {
        type: downgradeName as DowngradeDetailsTypeE,
        displayName: isAddon
          ? downgradeName
          : // TODO: create enum for plansTitleList
            `${(plansTitleList as any)[downgradeName]} ${getTextByKey(
              isYearly ? "yearly" : "monthly"
            )}`,
        price: strPrice,
        quantity: newQuantity,
        isYearly: isYearly,
        nextBillingDate: nextBillingDate,
      };

      dispatch(setDowngradeDetails(downgradeDetailsTemp));
    }
  };

  const _displayToast = (toastTextKey: string, appearance: AppearanceTypes) => {
    toast.addToast(getTextByKey(toastTextKey), {
      appearance,
    });
  };

  /**
   * Handles the downgrade of an addon transaction.
   */
  const downgradeAddonTransaction = useCallback(() => {
    const payload: DowngradeAddonPayloadT = {
      path: getPath(),
      displayToast: _displayToast,
      addonQuantity: addonQuantity,
    };

    dispatch(downgradeAddon(payload));
  }, [getUserData()?.UserId, getPath(), addonQuantity]);

  /**
   * Handles the downgrade subscription transaction.
   */
  const downgradeSubscriptionTransaction = useCallback(() => {
    const payload: DowngradeSubscriptionPayloadT = {
      path: getPath(),
      displayToast: _displayToast,
      periodType: periodType!,
      subscriptionType: packageType!,
    };

    dispatch(downgradeSubscription(payload));
  }, [getUserData()?.UserId, getPath(), periodType, packageType]);

  /**
   * Clears the error message for the downgrade addon.
   */
  const clearDowngradeAddonErrorMessage = () => {
    dispatch(setDowngradeAddonErrorMessage(""));
  };

  return {
    validateDowngradeAddons,
    validateDowngradeSubscriptions,
    updateDowngradeDetails,
    downgradeDetails,
    setDowngradeDetails: updateDowngradeDetails,
    downgradeAddon: downgradeAddonTransaction,
    downgradeSubscription: downgradeSubscriptionTransaction,
    isDowngrade,
    downgradeAddonErrorMessage,
    clearDowngradeAddonErrorMessage,
  };
};

export default useDowngrade;
